import { supabase } from "@/integrations/supabase/client";
import { 
  send24HourReminder,
  send1HourReminder,
  formatAppointmentDate,
  formatAppointmentTime
} from "@/lib/mailchimp-calendar";

/**
 * Cancel all pending reminders for an appointment
 */
export const cancelAppointmentReminders = async (appointmentId: string) => {
  try {
    const { error } = await supabase
      .from('email_notifications')
      .update({ status: 'cancelled' })
      .eq('status', 'pending')
      .like('template_variables->appointment_id', `%${appointmentId}%`);
    
    if (error) {
      console.error('Error cancelling reminders:', error);
      return { success: false, error };
    }
    
    console.log(`✅ Cancelled pending reminders for appointment ${appointmentId}`);
    return { success: true };
  } catch (error) {
    console.error('Error in cancelAppointmentReminders:', error);
    return { success: false, error };
  }
};

interface ScheduleRemindersData {
  appointmentId: string;
  contactId: string;
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  scheduledDate: string;
  scheduledTime: string;
  meetingLink?: string;
}

export const scheduleAppointmentReminders = async (data: ScheduleRemindersData) => {
  try {
    // Parse the appointment datetime
    const appointmentDateTime = new Date(`${data.scheduledDate}T${data.scheduledTime}`);
    
    // Calculate reminder times
    const reminder24h = new Date(appointmentDateTime.getTime() - 24 * 60 * 60 * 1000);
    const reminder1h = new Date(appointmentDateTime.getTime() - 60 * 60 * 1000);
    
    // Format date and time for Mailchimp templates
    const formattedDate = formatAppointmentDate(data.scheduledDate);
    const formattedTime = formatAppointmentTime(data.scheduledTime, data.scheduledDate);
    
    const emailData = {
      customerEmail: data.email,
      customerName: `${data.firstName} ${data.lastName}`,
      appointmentDate: formattedDate,
      appointmentTime: formattedTime,
      appointmentId: data.appointmentId
    };
    
    // Store reminder metadata in database for tracking
    // Schedule 24-hour reminder metadata
    const { error: reminder24hError } = await supabase
      .from('email_notifications')
      .insert({
        to_email: data.email,
        to_name: `${data.firstName} ${data.lastName}`,
        subject: 'Reminder: Demo Tomorrow - MuxSignal Wireless',
        body_html: '<!-- Mailchimp managed template -->',
        body_text: 'Reminder email sent via Mailchimp',
        email_type: 'appointment_reminder_24h',
        scheduled_send_time: reminder24h.toISOString(),
        status: 'pending',
        contact_id: data.contactId,
        template_variables: {
          appointment_id: data.appointmentId,
          CUSTOMER_NAME: `${data.firstName} ${data.lastName}`,
          APPOINTMENT_DATE: formattedDate,
          APPOINTMENT_TIME: formattedTime,
          MEETING_LINK: data.meetingLink || '',
          DOWNLOAD_LINK: `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/calendar-download?contact_id=${data.contactId}`,
          RESCHEDULE_LINK: `https://simnetwireless.com/reschedule/${data.appointmentId}`,
          mailchimp_managed: true
        }
      });
    
    if (reminder24hError) {
      console.error('Error logging 24h reminder:', reminder24hError);
    }
    
    // Schedule 1-hour reminder metadata
    const { error: reminder1hError } = await supabase
      .from('email_notifications')
      .insert({
        to_email: data.email,
        to_name: `${data.firstName} ${data.lastName}`,
        subject: 'Starting Soon: Your Demo in 1 Hour - MuxSignal Wireless',
        body_html: '<!-- Mailchimp managed template -->',
        body_text: 'Reminder email sent via Mailchimp',
        email_type: 'appointment_reminder_1h',
        scheduled_send_time: reminder1h.toISOString(),
        status: 'pending',
        contact_id: data.contactId,
        template_variables: {
          appointment_id: data.appointmentId,
          CUSTOMER_NAME: `${data.firstName} ${data.lastName}`,
          APPOINTMENT_DATE: formattedDate,
          APPOINTMENT_TIME: formattedTime,
          MEETING_LINK: data.meetingLink || '',
          DOWNLOAD_LINK: `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/calendar-download?contact_id=${data.contactId}`,
          RESCHEDULE_LINK: `https://simnetwireless.com/reschedule/${data.appointmentId}`,
          mailchimp_managed: true
        }
      });
    
    if (reminder1hError) {
      console.error('Error logging 1h reminder:', reminder1hError);
    }
    
    // Schedule internal team 24-hour reminder
    const { error: internalReminder24hError } = await supabase
      .from('email_notifications')
      .insert({
        to_email: 'internal-team@reminder',
        subject: `Reminder: Appointment Tomorrow - ${data.firstName} ${data.lastName}`,
        body_html: '<!-- Mailchimp managed template -->',
        body_text: 'Internal team reminder sent via Mailchimp',
        email_type: 'internal_reminder_24h',
        scheduled_send_time: reminder24h.toISOString(),
        status: 'pending',
        contact_id: data.contactId,
        template_variables: {
          appointment_id: data.appointmentId,
          CUSTOMER_NAME: `${data.firstName} ${data.lastName}`,
          CUSTOMER_EMAIL: data.email,
          CUSTOMER_PHONE: data.phone || '',
          APPOINTMENT_DATE: formattedDate,
          APPOINTMENT_TIME: formattedTime,
          reminder_type: '24h',
          mailchimp_managed: true,
          internal: true
        }
      });
    
    if (internalReminder24hError) {
      console.error('Error logging internal 24h reminder:', internalReminder24hError);
    }
    
    // Schedule internal team 1-hour reminder
    const { error: internalReminder1hError } = await supabase
      .from('email_notifications')
      .insert({
        to_email: 'internal-team@reminder',
        subject: `STARTING SOON: ${data.firstName} ${data.lastName} in 1 Hour`,
        body_html: '<!-- Mailchimp managed template -->',
        body_text: 'Internal team reminder sent via Mailchimp',
        email_type: 'internal_reminder_1h',
        scheduled_send_time: reminder1h.toISOString(),
        status: 'pending',
        contact_id: data.contactId,
        template_variables: {
          appointment_id: data.appointmentId,
          CUSTOMER_NAME: `${data.firstName} ${data.lastName}`,
          CUSTOMER_EMAIL: data.email,
          CUSTOMER_PHONE: data.phone || '',
          APPOINTMENT_TIME: formattedTime,
          reminder_type: '1h',
          mailchimp_managed: true,
          internal: true
        }
      });
    
    if (internalReminder1hError) {
      console.error('Error logging internal 1h reminder:', internalReminder1hError);
    }
    
    // Note: Actual email sending happens via cron job that reads pending emails
    // or can be triggered immediately for testing
    
    console.log('✅ Appointment reminders scheduled via Mailchimp (customer + internal team)');
    return { success: true };
  } catch (error) {
    console.error('Error in scheduleAppointmentReminders:', error);
    return { success: false, error };
  }
};
